import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAdminAuth } from "@/admin/AdminAuthContext";
import { Loader2, ShieldCheck, Lock, Mail, Eye, EyeOff } from "lucide-react";

function formatError(detail) {
  if (!detail) return "Erreur. Réessayez.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" · ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function AdminLogin() {
  const { user, login } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // If user becomes authenticated (after login OR if already logged in),
  // redirect to the originally requested page or /admin.
  useEffect(() => {
    if (user && typeof user === "object") {
      const target = location.state?.from?.pathname || "/admin";
      navigate(target, { replace: true });
    }
  }, [user, navigate, location.state]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(email.trim(), password);
      // Redirect happens via the effect above as soon as `user` updates.
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#050507] text-white flex items-center justify-center px-6 relative overflow-hidden"
      data-testid="admin-login"
    >
      {/* Decorative glows */}
      <div
        aria-hidden
        className="absolute -top-32 -left-32 w-[420px] h-[420px] rounded-full bg-[#E52431]/12 blur-3xl"
      />
      <div
        aria-hidden
        className="absolute -bottom-32 -right-32 w-[500px] h-[500px] rounded-full bg-[#1F2C6E]/18 blur-3xl"
      />

      <form
        onSubmit={onSubmit}
        className="relative w-full max-w-md p-8 rounded-3xl border border-white/10 bg-white/[0.02] backdrop-blur-xl"
      >
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#E52431] to-[#1F2C6E] flex items-center justify-center">
            <ShieldCheck size={18} className="text-white" />
          </div>
          <div>
            <div className="text-xs tracking-[2px] text-white/40 font-semibold">
              EM GROUP
            </div>
            <div className="text-sm font-semibold">Espace Admin</div>
          </div>
        </div>

        <h1 className="font-outfit text-2xl font-light leading-tight">
          Connexion à l&apos;<span className="gradient-text font-medium">outil de prospection</span>
        </h1>
        <p className="text-white/55 text-sm mt-2 font-light">
          Accès strictement réservé.
        </p>

        <div className="mt-7 space-y-4">
          <label className="block" data-testid="admin-login-email-field">
            <span className="text-xs uppercase tracking-[0.15em] text-white/55 font-medium inline-flex items-center gap-2">
              <Mail size={12} /> Email
            </span>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              data-testid="admin-login-email-input"
              className="mt-2 w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:border-[#E52431]/60 outline-none transition"
            />
          </label>
          <label className="block" data-testid="admin-login-password-field">
            <span className="text-xs uppercase tracking-[0.15em] text-white/55 font-medium inline-flex items-center gap-2">
              <Lock size={12} /> Mot de passe
            </span>
            <div className="relative mt-2">
              <input
                type={showPassword ? "text" : "password"}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                data-testid="admin-login-password-input"
                className="w-full bg-white/5 border border-white/10 rounded-xl pl-4 pr-12 py-3 text-white placeholder-white/30 focus:border-[#E52431]/60 outline-none transition"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                data-testid="admin-login-password-toggle"
                tabIndex={-1}
                aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded text-white/45 hover:text-white hover:bg-white/5 transition"
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </label>
        </div>

        {error && (
          <div
            className="mt-5 p-3 rounded-xl border border-[#E52431]/30 bg-[#E52431]/10 text-sm text-[#FFB4BB]"
            data-testid="admin-login-error"
          >
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-submit"
          className="mt-7 w-full inline-flex items-center justify-center gap-2 rounded-full gradient-bg text-white font-semibold py-4 text-sm uppercase tracking-[0.15em] hover:shadow-[0_0_50px_rgba(229,36,49,0.5)] transition btn-shine disabled:opacity-60"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          {loading ? "Connexion…" : "Se connecter"}
        </button>
      </form>
    </div>
  );
}
